import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { useT } from "../../i18n/index.js";
import type { MessageKey } from "../../i18n/index.js";
import { useAuth } from "../../hooks/useAuth.js";
import { formatMoney } from "../../lib/format-fr.js";
import { trpc } from "../../lib/trpc.js";

/**
 * F-home-briefing (CEO 2026-10-22, brief "Home do Gestion V3", fase 1) — o
 * topo do Painel vira um briefing: saudação + semana, "Precisa de você"
 * (cartões com contador e ação; zerado some), 4 tiles numéricos, chantiers
 * em curso (barra entregues/total) e "Entrou hoje". Só dados existentes;
 * nada decorativo; enquanto carrega, esqueleto — nunca um zero.
 */

export interface HomeTodo {
  icon: LucideIcon;
  bg: string;
  fg: string;
  t: string;
  s: string;
  href: string;
}

interface DegarnData {
  total: number;
  totalHt: number;
  foraDaFaturaHt: number;
}

interface Props {
  companyId: number | null;
  grantOnly: boolean;
  intl: string;
  todos: HomeTodo[];
  degarn: DegarnData | undefined;
}

/** Semana ISO 8601 (segunda-feira como início). */
export function isoWeek(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

const DISPLAY = "var(--font-display)";
const money2 = (v: number) => formatMoney(v, { decimals: 2 });

function Skeleton({ w = "60%", h = 14 }: { w?: string | number; h?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: w,
        height: h,
        borderRadius: 4,
        background: "var(--surface-muted)",
      }}
    />
  );
}

function Card({
  tone,
  title,
  count,
  children,
  action,
  href,
  testid,
}: {
  tone: "warn" | "danger" | "navy" | "faint";
  title: string;
  count: number | string;
  children?: React.ReactNode;
  action: string;
  href: string;
  testid: string;
}) {
  const border =
    tone === "warn"
      ? "var(--warning-500)"
      : tone === "danger"
        ? "var(--danger-500)"
        : tone === "navy"
          ? "var(--navy-800)"
          : "var(--text-muted)";
  return (
    <article
      className="panel"
      data-testid={testid}
      style={{
        borderLeft: `5px solid ${border}`,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        margin: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
        <div
          className="num"
          style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 24, lineHeight: 1 }}
        >
          {count}
        </div>
      </div>
      {children && (
        <div style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
          {children}
        </div>
      )}
      <Link
        href={href}
        className="btn btn--primary btn--sm"
        style={{ alignSelf: "flex-start", minHeight: 32 }}
      >
        {action} →
      </Link>
    </article>
  );
}

function Tile({
  value,
  label,
  href,
  inverted,
  loading,
  testid,
}: {
  value: string;
  label: string;
  href: string;
  inverted?: boolean;
  loading?: boolean;
  testid: string;
}) {
  return (
    <Link
      href={href}
      className="panel"
      data-testid={testid}
      style={{
        textDecoration: "none",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        margin: 0,
        ...(inverted ? { background: "var(--navy-800)", borderColor: "var(--navy-800)" } : {}),
      }}
    >
      <span
        className="num"
        style={{
          fontFamily: DISPLAY,
          fontWeight: 700,
          fontSize: 30,
          lineHeight: 1,
          color: inverted ? "var(--lime)" : "var(--text-strong)",
        }}
      >
        {loading ? <Skeleton w="50%" h={26} /> : value}
      </span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "1.1px",
          textTransform: "uppercase",
          color: inverted ? "var(--lime)" : "var(--text-muted)",
        }}
      >
        {label}
      </span>
    </Link>
  );
}

const FEED_KEY: Record<string, MessageKey> = {
  portal_claim: "dashboard.feedClaim",
  office_launch: "dashboard.feedOfficeLaunch",
  chantier_check_reception: "dashboard.feedReception",
  chantier_check_delivery: "dashboard.feedDelivery",
  portal_devis_import: "dashboard.feedDevisImport",
  edit_devis: "dashboard.feedDevisEdit",
  issue: "dashboard.feedInvoiceIssued",
  reissue: "dashboard.feedInvoiceReissued",
  cancel: "dashboard.feedInvoiceCancelled",
  mark_paid: "dashboard.feedMarkPaid",
  create: "dashboard.feedCreditNote",
  wise_sync_completed: "dashboard.feedWiseOk",
  wise_sync_failed: "dashboard.feedWiseFail",
  reception_doc_upload: "dashboard.feedDocUpload",
  email_sent: "dashboard.feedEmailSent",
  franchise_derogation: "dashboard.feedDerogation",
};

const FEED_DOT: Record<string, string> = {
  portal_claim: "var(--warning-500)",
  office_launch: "var(--warning-500)",
  chantier_check_reception: "var(--navy-800)",
  chantier_check_delivery: "var(--navy-800)",
  portal_devis_import: "var(--lime)",
  edit_devis: "var(--lime)",
  issue: "var(--success-500)",
  reissue: "var(--success-500)",
  mark_paid: "var(--success-500)",
  create: "var(--warning-500)",
  cancel: "var(--danger-500)",
  wise_sync_failed: "var(--danger-500)",
};

export function HomeBriefing({ companyId, grantOnly, intl, todos, degarn }: Props) {
  const t = useT();
  const enabled = companyId != null;
  const briefingQ = trpc.home.briefing.useQuery(
    { companyId: companyId ?? 0 },
    { enabled, staleTime: 30_000 },
  );
  // Mesma consulta (e mesmo cache) do quadro "Carros na operação".
  const boardQ = trpc.dossiers.operationBoard.useQuery(
    { companyId: companyId ?? 0, includeEntregues: true },
    { enabled },
  );
  const b = briefingQ.data;
  // CEO 2026-10-31 — "+ N clientes" abre o resto da faixa laranja (sem sair do Painel).
  const [verTodosAFaturar, setVerTodosAFaturar] = useState(false);
  const board = boardQ.data?.cars ?? [];

  // CEO 2026-10-28 — a saudação é para a PESSOA, não para a empresa:
  // primeiro nome da conta (as contas ficam em CAIXA ALTA → "Landione").
  const { user } = useAuth();
  const primeiroNome = (() => {
    const n = (user?.name ?? "").trim().split(/\s+/)[0] ?? "";
    if (!n) return "Euro Storm";
    return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
  })();
  // F-tarefas (CEO 2026-10-29) — "basta o sino e o Painel": as minhas
  // (atrasadas + hoje) e, para quem cria, as atrasadas da equipe.
  const myTasksQ = trpc.tasks.mine.useQuery(undefined, { enabled: !!user, staleTime: 30_000 });
  const isCompanyAdmin =
    !!user &&
    companyId != null &&
    (user.isSuperAdmin ||
      user.memberships.some((m) => m.active && m.companyId === companyId && m.role === "admin"));
  const teamTasksQ = trpc.tasks.teamOverdue.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: enabled && isCompanyAdmin, staleTime: 30_000 },
  );
  // F-troca-cliente-devis (CEO 2026-10-30) — devis reenviado com outro cliente.
  const pendingCustQ = trpc.dossiers.pendingCustomerChanges.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: enabled && !grantOnly, staleTime: 30_000 },
  );
  const pendingCust = pendingCustQ.data?.rows ?? [];
  const hasPendingCust = pendingCust.length > 0;
  // CEO 2026-11-01 — faturas emitidas que ninguém enviou ao cliente.
  const unsentQ = trpc.customerInvoices.unsentSummary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: enabled && !grantOnly, staleTime: 30_000 },
  );
  const unsent = unsentQ.data;
  const hasUnsent = (unsent?.count ?? 0) > 0;
  const myTasks = myTasksQ.data;
  const nMyTasks = (myTasks?.overdue ?? 0) + (myTasks?.today ?? 0);
  const hasMyTasks = nMyTasks > 0;
  const teamTasks = isCompanyAdmin ? teamTasksQ.data : undefined;
  const hasTeamTasks = (teamTasks?.total ?? 0) > 0;
  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 12
      ? t("dashboard.greetingMorning")
      : hour < 18
        ? t("dashboard.greetingAfternoon")
        : t("dashboard.greetingEvening");
  const dateLabel = now.toLocaleDateString(intl, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const carsOpen = board.filter((c) => c.status !== "entregue").length;
  const deliveriesPending = board.filter(
    (c) => c.chantierId != null && c.status === "reparado",
  ).length;
  const loadingTiles = !b || boardQ.isLoading;

  // Cartões "Precisa de você": só os que têm conteúdo.
  const hasToInvoice = (b?.toInvoiceCount ?? 0) > 0;
  const hasFranchise = (b?.franchiseMissing.length ?? 0) > 0;
  const hasDegarn = (degarn?.total ?? 0) > 0;
  // CEO 2026-10-27 (opção C) — cliente do eDSP a revisar com carro em andamento.
  const hasReview = (b?.customersToReview.length ?? 0) > 0;
  const nothingPending =
    b != null &&
    !hasToInvoice &&
    !hasFranchise &&
    !hasDegarn &&
    !hasReview &&
    !hasMyTasks &&
    !hasTeamTasks &&
    !hasPendingCust &&
    !hasUnsent &&
    todos.length === 0;
  // CEO 2026-10-22: "Veículos a faturar" NÃO entra na grade — volta a ser a
  // faixa de largura inteira de antes (mesmas cores e linhas por cliente).
  const gridHasCards =
    hasFranchise ||
    hasDegarn ||
    hasReview ||
    hasMyTasks ||
    hasTeamTasks ||
    hasPendingCust ||
    hasUnsent ||
    todos.length > 0;

  const fmtDate = (s: string | null) =>
    s
      ? new Date(s + "T00:00:00").toLocaleDateString(intl, { day: "2-digit", month: "2-digit" })
      : null;
  const fmtTime = (s: string) =>
    new Date(s).toLocaleTimeString(intl, { hour: "2-digit", minute: "2-digit" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22, marginBottom: 22 }}>
      {/* Saudação + data + semana */}
      <div
        className="panel"
        data-testid="home-greeting"
        style={{
          margin: 0,
          padding: "14px 18px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>
            {greeting}, {primeiroNome}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {dateLabel} ·{" "}
            <strong style={{ color: "var(--text-strong)" }}>
              {t("dashboard.weekPre")} {isoWeek(now)}
            </strong>
          </div>
        </div>
        {!grantOnly && (
          <Link href="/dossiers" className="btn btn--primary btn--sm" data-testid="home-new">
            + {t("dashboard.newDossier")}
          </Link>
        )}
      </div>

      {/* F-ops-home — "Il y a des voitures à facturer" (padrão eDSP), faixa de
          largura inteira como era antes do briefing (CEO 2026-10-22: "mantenha
          como era antes"). A LINHA INTEIRA é o link (CEO 2026-10-06). */}
      {b && hasToInvoice && (
        <div
          role="alert"
          data-testid="home-card-to-invoice"
          style={{
            background: "#FFF7ED",
            border: "1px solid #EA580C",
            borderLeft: "5px solid #EA580C",
            borderRadius: 10,
            padding: "12px 16px",
          }}
        >
          <p style={{ fontWeight: 800, fontSize: 14, color: "#9A3412", marginBottom: 8 }}>
            🛒 {t("dashboard.toInvoiceTitle")}
          </p>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {[...b.toInvoice, ...(verTodosAFaturar ? (b.toInvoiceRest ?? []) : [])].map((r, i) => (
              <Link
                key={r.customerId}
                href={`/dossiers?cliente=${r.customerId}&status=pending&reparados=1`}
                className="to-invoice-row"
                data-testid="to-invoice-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "7px 8px",
                  borderTop: i === 0 ? "none" : "1px solid #FDBA74",
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                <span style={{ color: "#7C2D12", fontSize: 13.5, fontWeight: 600 }}>
                  {/* CEO 2026-10-15: nome sempre em CAIXA ALTA. */}
                  <span style={{ textTransform: "uppercase" }}>{r.customerName}</span>{" "}
                  <span style={{ opacity: 0.7, fontWeight: 400 }}>
                    ({r.count} {r.count === 1 ? t("dashboard.carWord") : t("dashboard.carsWord")})
                  </span>
                </span>
                <span
                  style={{
                    background: "#EA580C",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "4px 14px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("dashboard.accessBtn")} »
                </span>
              </Link>
            ))}
            {/* CEO 2026-10-27 — clientes além dos 12 maiores não somem mais. */}
            {b.toInvoiceMore && (
              <button
                type="button"
                onClick={() => setVerTodosAFaturar((v) => !v)}
                aria-expanded={verTodosAFaturar}
                className="to-invoice-row"
                data-testid="to-invoice-toggle"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "7px 8px",
                  borderTop: "1px solid #FDBA74",
                  borderRadius: 6,
                  textDecoration: "none",
                  borderLeft: "none",
                  borderRight: "none",
                  borderBottom: "none",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                <span style={{ color: "#7C2D12", fontSize: 13.5, fontWeight: 600 }}>
                  {verTodosAFaturar ? "−" : "+"} {b.toInvoiceMore.customers}{" "}
                  {t("dashboard.toInvoiceMoreCustomers")}{" "}
                  <span style={{ opacity: 0.7, fontWeight: 400 }}>
                    ({b.toInvoiceMore.cars}{" "}
                    {b.toInvoiceMore.cars === 1 ? t("dashboard.carWord") : t("dashboard.carsWord")}{" "}
                    · {money2(b.toInvoiceMore.totalHt)} HT)
                  </span>
                </span>
                <span
                  style={{
                    background: "#FFFFFF",
                    color: "#EA580C",
                    border: "1px solid #EA580C",
                    borderRadius: 8,
                    padding: "3px 13px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {verTodosAFaturar
                    ? `${t("dashboard.toInvoiceShowLess")} ▴`
                    : `${t("dashboard.toInvoiceSeeAll")} ▾`}
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* PRECISA DE VOCÊ — some quando a única pendência é a faixa "a faturar". */}
      {(!b || nothingPending || gridHasCards) && (
        <section
          aria-labelledby="home-needs"
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <h2
            id="home-needs"
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              color: "var(--text-muted)",
            }}
          >
            {t("dashboard.needsYouTitle")}
          </h2>
          {!b && (
            <div className="panel" style={{ margin: 0, padding: 14 }}>
              <Skeleton w="40%" />
            </div>
          )}
          {nothingPending && (
            <p
              className="panel"
              data-testid="home-nothing-pending"
              style={{ margin: 0, padding: "10px 14px", fontSize: 13, color: "var(--text-muted)" }}
            >
              ✓ {t("dashboard.nothingPendingLine")}
            </p>
          )}
          {b && gridHasCards && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 14,
              }}
            >
              {hasMyTasks && myTasks && (
                <Card
                  tone={myTasks.overdue > 0 ? "danger" : "navy"}
                  title={t("tasks.myCardTitle")}
                  count={nMyTasks}
                  action={t("tasks.openTasks")}
                  href="/tarefas"
                  testid="home-card-my-tasks"
                >
                  {/* Recorrente atrasada vários dias = uma linha "título ×N". */}
                  {[
                    ...myTasks.rows
                      .filter((r) => r.bucket !== "upcoming")
                      .reduce((m, r) => {
                        const k = `${r.taskId}-${r.bucket}`;
                        const cur = m.get(k);
                        m.set(k, { r, n: (cur?.n ?? 0) + 1 });
                        return m;
                      }, new Map<string, { r: (typeof myTasks.rows)[number]; n: number }>())
                      .values(),
                  ]
                    .slice(0, 3)
                    .map(({ r, n }) => (
                      <span key={`${r.taskId}-${r.bucket}`} style={{ display: "block" }}>
                        {r.bucket === "overdue" ? (
                          <b style={{ color: "var(--danger-500)" }}>{t("tasks.st_overdue")}</b>
                        ) : (
                          t("tasks.groupToday")
                        )}{" "}
                        ·{" "}
                        <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>
                          {r.title}
                        </span>
                        {n > 1 && ` ×${n}`}
                      </span>
                    ))}
                </Card>
              )}
              {hasTeamTasks && teamTasks && (
                <Card
                  tone="danger"
                  title={t("tasks.teamOverdueTitle")}
                  count={teamTasks.total}
                  action={t("tasks.openTasks")}
                  href="/tarefas?view=equipe"
                  testid="home-card-team-tasks"
                >
                  {teamTasks.rows.slice(0, 4).map((p) => (
                    <span key={p.userId} style={{ display: "block" }}>
                      <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>{p.name}</span>{" "}
                      · {p.count}
                    </span>
                  ))}
                </Card>
              )}
              {hasUnsent && unsent && (
                <Card
                  tone="warn"
                  title={t("dashboard.unsentCardTitle")}
                  count={unsent.count}
                  action={t("dashboard.unsentCardAction")}
                  href="/faturas?naoEnviadas=1"
                  testid="home-card-unsent"
                >
                  {unsent.rows.slice(0, 3).map((r) => (
                    <span key={r.id} style={{ display: "block" }}>
                      <span
                        style={{
                          fontFamily: DISPLAY,
                          fontWeight: 600,
                          fontSize: 13,
                          color: "var(--text-strong)",
                        }}
                      >
                        {r.invoiceNumber}
                      </span>{" "}
                      · {r.customerName ?? "—"}
                    </span>
                  ))}
                </Card>
              )}
              {hasPendingCust && (
                <Card
                  tone="warn"
                  title={t("dashboard.pendingCustomerTitle")}
                  count={pendingCust.length}
                  action={t("dashboard.pendingCustomerAction")}
                  href={`/dossiers/${pendingCust[0]!.id}`}
                  testid="home-card-pending-customer"
                >
                  {pendingCust.slice(0, 3).map((r) => (
                    <Link
                      key={r.id}
                      href={`/dossiers/${r.id}`}
                      style={{ display: "block", color: "inherit", textDecoration: "none" }}
                    >
                      <span
                        style={{
                          fontFamily: DISPLAY,
                          fontWeight: 600,
                          fontSize: 13,
                          color: "var(--text-strong)",
                        }}
                      >
                        {r.plate ?? r.dossierCode}
                      </span>{" "}
                      · {r.currentName} → <b>{r.proposedName}</b>
                    </Link>
                  ))}
                </Card>
              )}
              {hasFranchise && (
                <Card
                  tone="navy"
                  title={t("dashboard.cardFranchise")}
                  count={b.franchiseMissing.length}
                  action={t("dashboard.actionInformFranchise")}
                  href={`/dossiers/${b.franchiseMissing[0]!.id}`}
                  testid="home-card-franchise"
                >
                  {b.franchiseMissing.slice(0, 3).map((r) => (
                    <Link
                      key={r.id}
                      href={`/dossiers/${r.id}`}
                      style={{ display: "block", color: "inherit", textDecoration: "none" }}
                    >
                      <span
                        style={{
                          fontFamily: DISPLAY,
                          fontWeight: 600,
                          fontSize: 13,
                          color: "var(--text-strong)",
                        }}
                      >
                        {r.plate ?? r.vin ?? `#${r.id}`}
                      </span>{" "}
                      · {r.vehicle || "—"}
                      {r.chantierName ? ` · ${r.chantierName}` : ` · ${r.customerName}`}
                    </Link>
                  ))}
                </Card>
              )}
              {hasReview && (
                <Card
                  tone="navy"
                  title={t("customerReview.homeTitle")}
                  count={b.customersToReview.length}
                  action={t("customerReview.homeAction")}
                  href="/clientes?revisar=1"
                  testid="home-card-customer-review"
                >
                  {b.customersToReview.slice(0, 3).map((c) => (
                    <Link
                      key={c.id}
                      href={`/clientes/${c.id}`}
                      style={{ display: "block", color: "inherit", textDecoration: "none" }}
                    >
                      <span
                        style={{
                          fontFamily: DISPLAY,
                          fontWeight: 600,
                          fontSize: 13,
                          color: "var(--text-strong)",
                        }}
                      >
                        {c.name}
                      </span>{" "}
                      · {c.openDossiers} {t("customerReview.homeCarsWord")}
                    </Link>
                  ))}
                </Card>
              )}
              {hasDegarn && degarn && (
                <Card
                  tone="navy"
                  title={t("dashboard.degarnTitle")}
                  count={degarn.total}
                  action={t("dashboard.degarnOpenList")}
                  href="/dossiers?degarn=1"
                  testid="home-card-degarn"
                >
                  {t("dashboard.degarnTotalPre")}{" "}
                  <span className="num">{money2(degarn.totalHt)}</span>
                  {degarn.foraDaFaturaHt > 0 && (
                    <>
                      {" · "}
                      {t("dashboard.degarnOutOfInvoicePre")}{" "}
                      <span className="num">{money2(degarn.foraDaFaturaHt)}</span>
                    </>
                  )}
                </Card>
              )}
              {todos.map((it, i) => {
                const Icon = it.icon;
                const danger = it.fg.includes("danger");
                return (
                  <Card
                    key={i}
                    tone={danger ? "danger" : "faint"}
                    title={it.t}
                    count={
                      (
                        <Icon size={20} style={{ color: it.fg }} aria-hidden="true" />
                      ) as unknown as string
                    }
                    action={t("dashboard.open")}
                    href={it.href}
                    testid={`home-card-todo-${i}`}
                  >
                    {it.s}
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* TILES */}
      <section
        aria-label={t("dashboard.tilesAria")}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 14,
        }}
      >
        <Tile
          value={`${money2(b?.toInvoiceTotalHt ?? 0)}`}
          label={t("dashboard.tileToInvoice")}
          href="/dossiers?status=pending&reparados=1"
          loading={!b}
          testid="home-tile-to-invoice"
        />
        <Tile
          value={String(carsOpen)}
          label={t("dashboard.tileOpenCars")}
          href="/dossiers?status=pending"
          loading={loadingTiles}
          testid="home-tile-open"
        />
        <Tile
          value={String(b?.chantiers.length ?? 0)}
          label={t("dashboard.tileActiveChantiers")}
          href="/chantiers"
          loading={!b}
          testid="home-tile-chantiers"
        />
        <Tile
          value={String(deliveriesPending)}
          label={t("dashboard.tilePendingDeliveries")}
          href="/dossiers?reparados=1"
          inverted
          loading={loadingTiles}
          testid="home-tile-deliveries"
        />
      </section>

      {/* CHANTIERS + ENTROU HOJE */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 22, alignItems: "stretch" }}>
        <section
          className="panel"
          aria-labelledby="home-chantiers"
          data-testid="home-chantiers"
          style={{
            flex: "999 1 560px",
            minWidth: 0,
            margin: 0,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <h2
            id="home-chantiers"
            style={{
              margin: "0 0 8px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              color: "var(--text-muted)",
            }}
          >
            {t("dashboard.chantiersTitle")}
          </h2>
          {!b && <Skeleton w="70%" />}
          {b && b.chantiers.length === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
              {t("dashboard.chantiersEmpty")}
            </p>
          )}
          {b &&
            b.chantiers.map((ch, i) => {
              const cars = board.filter((c) => c.chantierId === ch.id);
              const total = cars.length;
              const done = cars.filter((c) => c.status === "entregue").length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const techs = [...new Set(cars.flatMap((c) => c.technicians))];
              const fim = fmtDate(ch.endDate);
              const inicio = fmtDate(ch.startDate);
              return (
                <Link
                  key={ch.id}
                  href={`/chantiers/${ch.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "10px 0",
                    borderTop: i === 0 ? "none" : "1px solid var(--line-2, var(--surface-muted))",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {ch.name}
                      {ch.city && (
                        <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>
                          {" "}
                          · {ch.city}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {[
                        ch.managers,
                        techs.length > 0 ? techs.join(", ") : null,
                        total === 0 && inicio
                          ? `${t("dashboard.chantierStartsPre")} ${inicio}`
                          : null,
                        fim
                          ? `${t("dashboard.chantierUntilPre")} ${fim}`
                          : t("dashboard.chantierNoEnd"),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <div
                    aria-hidden="true"
                    style={{
                      width: 150,
                      flex: "0 0 150px",
                      height: 7,
                      borderRadius: 4,
                      background: "var(--surface-muted)",
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: 7,
                        borderRadius: 4,
                        background: "var(--lime)",
                      }}
                    />
                  </div>
                  <div
                    className="num"
                    style={{
                      width: 58,
                      flex: "0 0 58px",
                      textAlign: "right",
                      fontFamily: DISPLAY,
                      fontWeight: 700,
                      fontSize: 16,
                    }}
                  >
                    {done}/{total}
                  </div>
                </Link>
              );
            })}
          {b && b.chantiers.length > 0 && (
            <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--text-muted)" }}>
              {t("dashboard.chantiersBarHint")}
            </p>
          )}
        </section>

        <section
          className="panel"
          aria-labelledby="home-feed"
          data-testid="home-feed"
          style={{
            flex: "1 1 300px",
            minWidth: 0,
            margin: 0,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <h2
            id="home-feed"
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              color: "var(--text-muted)",
            }}
          >
            {t("dashboard.feedTitle")}
          </h2>
          {!b && <Skeleton w="80%" />}
          {b && b.feed.length === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
              {t("dashboard.feedEmpty")}
            </p>
          )}
          {b && b.feed.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {b.feed.map((f) => {
                const key = FEED_KEY[f.action];
                const label = key ? t(key) : f.action;
                const ref = f.plate ?? f.invoiceNumber ?? f.creditNoteNumber ?? "";
                const href = f.dossierId
                  ? `/dossiers/${f.dossierId}`
                  : f.invoiceNumber
                    ? "/faturas"
                    : null;
                const detail = [
                  fmtTime(f.at),
                  f.technicianName,
                  f.userName,
                  f.action === "chantier_check_delivery"
                    ? f.workOk === "true"
                      ? t("dashboard.feedWorkOk")
                      : f.workOk === "false"
                        ? t("dashboard.feedWorkReserves")
                        : null
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                const inner = (
                  <>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: FEED_DOT[f.action] ?? "var(--text-muted)",
                        marginTop: 5,
                        flex: "0 0 7px",
                      }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 500 }}>
                        {label}
                        {ref && (
                          <span style={{ fontFamily: DISPLAY, fontWeight: 600, marginLeft: 6 }}>
                            {ref}
                          </span>
                        )}
                      </span>
                      <span style={{ display: "block", fontSize: 11, color: "var(--text-muted)" }}>
                        {detail}
                      </span>
                    </span>
                  </>
                );
                return (
                  <li key={f.id} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    {href ? (
                      <Link
                        href={href}
                        style={{
                          display: "flex",
                          gap: 10,
                          textDecoration: "none",
                          color: "inherit",
                          minWidth: 0,
                        }}
                      >
                        {inner}
                      </Link>
                    ) : (
                      inner
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {!grantOnly && (
            <div
              style={{
                borderTop: "1px solid var(--surface-muted)",
                paddingTop: 10,
                fontSize: 11,
                color: "var(--text-muted)",
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: "var(--text-strong)" }}>Facturation électronique</strong>
              <br />
              {t("dashboard.einvoiceLine")}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
